import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, Pill, Stethoscope, RotateCcw, Search, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import { useColumnResize } from "@/lib/useColumnResize";

const ROWS = 10;
const DEFAULT_WIDTHS = [180, 130, 130, 130]; // name | dose | start | end

function CourseRow({ row, index, onSave, onReset, savingIndex, kind, checked, onCheck }) {
  const { t } = useI18n();
  const [local, setLocal] = useState(row);
  const dirtyRef = useRef(false);

  useEffect(() => { setLocal(row); }, [row]);

  const setField = (patch) => {
    dirtyRef.current = true;
    setLocal((p) => ({ ...p, ...patch }));
  };
  const flush = async () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    await onSave(index, {
      name: local.name || "",
      dose: local.dose || "",
      start_date: local.start_date || "",
      end_date: local.end_date || "",
    });
  };

  const isSaving = savingIndex === index;
  const placeholderName = kind === "supplements"
    ? t("courses.supplement_name")
    : t("courses.medication_name");

  return (
    <tr data-testid={`${kind}-individual-row-${index}`}>
      <td className="sel" style={{ width: 30, textAlign: "center" }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(e.target.checked)}
          style={{ width: 15, height: 15, margin: "0 auto", accentColor: "#6b7245" }}
          data-testid={`select-${kind}-row-${index}`}
        />
      </td>
      <td className="rownum">{local.cage_no}</td>
      <td>
        <input
          value={local.name || ""}
          onChange={(e) => setField({ name: e.target.value })}
          onBlur={flush}
          placeholder={placeholderName}
          data-testid={`${kind}-ind-name-${index}`}
        />
      </td>
      <td>
        <input
          value={local.dose || ""}
          onChange={(e) => setField({ dose: e.target.value })}
          onBlur={flush}
          placeholder={t("courses.dose")}
          data-testid={`${kind}-ind-dose-${index}`}
        />
      </td>
      <td>
        <input
          type="date"
          value={local.start_date || ""}
          onChange={(e) => setField({ start_date: e.target.value })}
          onBlur={flush}
          data-testid={`${kind}-ind-start-${index}`}
        />
      </td>
      <td>
        <input
          type="date"
          value={local.end_date || ""}
          onChange={(e) => setField({ end_date: e.target.value })}
          onBlur={flush}
          data-testid={`${kind}-ind-end-${index}`}
        />
      </td>
      <td className="reset action" style={{ width: 36 }}>
        {isSaving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-espresso/40 mx-auto" />
        ) : (
          <button
            type="button"
            onClick={() => onReset(index)}
            title={t("courses.reset_row")}
            className="mx-auto flex items-center justify-center w-6 h-6 rounded text-espresso/30 hover:text-red-600 hover:bg-red-50 transition-colors"
            data-testid={`${kind}-row-reset-${index}`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

function ResizableTh({ index, width, startResize, startResizeTouch, children }) {
  return (
    <th style={{ width }}>
      {children}
      <span
        className="resize-handle"
        onMouseDown={(e) => startResize(e, index)}
        onTouchStart={(e) => startResizeTouch(e, index)}
      />
    </th>
  );
}

function CourseSection({ kind }) {
  const { activeGroup } = useGroups();
  const { t } = useI18n();
  const [individual, setIndividual] = useState([]);
  const [group, setGroup] = useState({ name: "", dose: "", start_date: "", end_date: "", remark: "" });
  const [loading, setLoading] = useState(true);
  const [savingIndex, setSavingIndex] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const groupDirtyRef = useRef(false);
  const { widths, startResize, startResizeTouch } = useColumnResize(`xls-${kind}-ind`, DEFAULT_WIDTHS);
  const { widths: gWidths, startResize: gStartResize, startResizeTouch: gStartResizeTouch } =
    useColumnResize(`xls-${kind}-grp`, DEFAULT_WIDTHS);

  const load = useCallback(async () => {
    if (!activeGroup?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/courses/${kind}`);
      setIndividual(data.individual || []);
      setGroup(data.group || { name: "", dose: "", start_date: "", end_date: "", remark: "" });
    } catch (_e) {
      void _e;
      toast.error("Offline — no cached data");
    } finally {
      setLoading(false);
    }
  }, [activeGroup?.id, kind]);

  useEffect(() => { load(); }, [load]);

  const saveIndividual = async (index, payload) => {
    setSavingIndex(index);
    try {
      await api.put(`/courses/${kind}/individual/${index}`, payload);
      setIndividual((arr) => {
        const c = [...arr];
        c[index] = { ...c[index], ...payload };
        return c;
      });
    } catch (_e) {
      void _e;
      toast.error("Save failed");
    } finally {
      setSavingIndex(null);
    }
  };

  const setGroupField = (patch) => {
    groupDirtyRef.current = true;
    setGroup((p) => ({ ...p, ...patch }));
  };
  const flushGroup = async () => {
    if (!groupDirtyRef.current) return;
    groupDirtyRef.current = false;
    setSavingGroup(true);
    try {
      await api.put(`/courses/${kind}/group`, {
        name: group.name || "",
        dose: group.dose || "",
        start_date: group.start_date || "",
        end_date: group.end_date || "",
        remark: group.remark || "",
      });
    } catch (_e) {
      void _e;
      toast.error("Save failed");
    } finally {
      setSavingGroup(false);
    }
  };

  const onResetRow = async (index) => {
    await saveIndividual(index, { name: "", dose: "", start_date: "", end_date: "" });
    toast.success(t("courses.reset_row_done"));
  };

  const onResetGroup = async () => {
    groupDirtyRef.current = false;
    setSavingGroup(true);
    try {
      await api.put(`/courses/${kind}/group`, {
        name: "", dose: "", start_date: "", end_date: "", remark: "",
      });
      setGroup({ name: "", dose: "", start_date: "", end_date: "", remark: "" });
      toast.success(t("courses.reset_row_done"));
    } catch (_e) {
      void _e;
      toast.error("Reset failed");
    } finally {
      setSavingGroup(false);
    }
  };

  const nameLabel = kind === "supplements" ? t("courses.supplement_name") : t("courses.medication_name");
  const Icon = kind === "supplements" ? Pill : Stethoscope;
  const [jumpCage, setJumpCage] = useState("");
  const wrapRef = useRef(null);
  const [selected, setSelected] = useState({});
  const [bulkCount, setBulkCount] = useState("10");
  const selCount = Object.values(selected).filter(Boolean).length;

  const bulkAdd = async () => {
    try {
      await api.post(`/courses/${kind}/bulk-add`, { count: parseInt(bulkCount, 10) });
      await load();
      toast.success(t("bulk.added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteSelected = async () => {
    if (!window.confirm(t("bulk.delete_confirm"))) return;
    const indices = Object.keys(selected).filter((k) => selected[k]).map(Number);
    setSelected({});
    try {
      await api.post(`/courses/${kind}/delete-rows`, { indices });
      await load();
      toast.success(t("bulk.deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const jumpTo = (value) => {
    setJumpCage(value);
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > individual.length || !wrapRef.current) return;
    const row = wrapRef.current.querySelector(`[data-testid="${kind}-individual-row-${n - 1}"]`);
    if (!row) return;
    row.scrollIntoView({ block: "center", behavior: "smooth" });
    row.style.transition = "background-color 0.3s";
    row.style.backgroundColor = "rgba(180, 190, 130, 0.35)";
    setTimeout(() => { row.style.backgroundColor = ""; }, 1600);
  };

  return (
    <div className="space-y-6" data-testid={`${kind}-section`}>
      {/* Individual */}
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Icon className="w-4 h-4 text-olive" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-olive">
            {t("courses.section_individual")}
          </h3>
          <div className="ml-auto rtl:ml-0 rtl:mr-auto flex items-center gap-1.5 flex-wrap">
            <select
              value={bulkCount}
              onChange={(e) => setBulkCount(e.target.value)}
              className="text-sm bg-white border border-app-border rounded-lg px-2 py-1 outline-none"
              data-testid={`${kind}-bulk-count`}
            >
              {[1, 5, 10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              type="button"
              onClick={bulkAdd}
              className="flex items-center gap-1 text-xs font-semibold bg-white border border-app-border rounded-lg px-2.5 py-1.5 hover:border-olive/50"
              data-testid={`${kind}-bulk-add`}
            >
              <Plus className="w-3.5 h-3.5" /> {t("bulk.add")}
            </button>
            <button
              type="button"
              onClick={deleteSelected}
              disabled={selCount === 0}
              className="flex items-center gap-1 text-xs font-semibold bg-white border border-red-300 text-red-600 rounded-lg px-2.5 py-1.5 hover:bg-red-50 disabled:opacity-40"
              data-testid={`${kind}-delete-rows`}
            >
              <Trash2 className="w-3.5 h-3.5" /> {t("bulk.delete")}{selCount > 0 ? ` (${selCount})` : ""}
            </button>
            <div className="flex items-center gap-1.5 bg-white border border-app-border rounded-lg px-2 py-1">
              <Search className="w-3.5 h-3.5 text-espresso/40" />
              <input
                type="number"
                min={1}
                max={individual.length || ROWS}
                value={jumpCage}
                onChange={(e) => jumpTo(e.target.value)}
                placeholder={t("courses.jump_cage")}
                className="w-24 text-sm bg-transparent outline-none placeholder:text-espresso/40"
                data-testid={`${kind}-cage-jump-input`}
              />
            </div>
          </div>
        </div>
        <div className="xls-wrap" style={{ maxHeight: "60vh" }} ref={wrapRef} data-testid={`${kind}-individual-table`}>
          <table className="xls">
            <thead>
              <tr>
                <th className="sel" style={{ width: 30, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={individual.length > 0 && selCount === individual.length}
                  onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(individual.map((_, i) => [i, true])) : {})}
                  title="Select all"
                  style={{ width: 15, height: 15, margin: "0 auto", accentColor: "#6b7245" }}
                  data-testid="select-all-courses"
                />
              </th>
                <th className="rownum">{t("courses.cage_no")}</th>
                <ResizableTh index={0} width={widths[0]} startResize={startResize} startResizeTouch={startResizeTouch}>
                  {nameLabel}
                </ResizableTh>
                <ResizableTh index={1} width={widths[1]} startResize={startResize} startResizeTouch={startResizeTouch}>
                  {t("courses.dose")}
                </ResizableTh>
                <ResizableTh index={2} width={widths[2]} startResize={startResize} startResizeTouch={startResizeTouch}>
                  {t("courses.start_date")}
                </ResizableTh>
                <ResizableTh index={3} width={widths[3]} startResize={startResize} startResizeTouch={startResizeTouch}>
                  {t("courses.end_date")}
                </ResizableTh>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline-block text-espresso/60" /></td></tr>
              ) : (
                individual.map((r, idx) => (
                  <CourseRow
                    key={r.cage_no}
                    row={r}
                    index={idx}
                    onSave={saveIndividual}
                    onReset={onResetRow}
                    savingIndex={savingIndex}
                    kind={kind}
                    checked={!!selected[idx]}
                    onCheck={(v) => setSelected((s) => ({ ...s, [idx]: v }))}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4 text-olive" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-olive">
            {t("courses.section_group")}
          </h3>
          {savingGroup && <Loader2 className="w-3.5 h-3.5 animate-spin text-espresso/40" />}
          <button
            type="button"
            onClick={onResetGroup}
            title={t("courses.reset_row")}
            className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-espresso/40 hover:text-red-600 transition-colors"
            data-testid={`${kind}-group-reset`}
          >
            <RotateCcw className="w-3 h-3" />
            {t("courses.reset_row")}
          </button>
        </div>
        <div className="xls-wrap" data-testid={`${kind}-group-table`}>
          <table className="xls">
            <thead>
              <tr>
                <ResizableTh index={0} width={gWidths[0]} startResize={gStartResize} startResizeTouch={gStartResizeTouch}>
                  {nameLabel}
                </ResizableTh>
                <ResizableTh index={1} width={gWidths[1]} startResize={gStartResize} startResizeTouch={gStartResizeTouch}>
                  {t("courses.dose")}
                </ResizableTh>
                <ResizableTh index={2} width={gWidths[2]} startResize={gStartResize} startResizeTouch={gStartResizeTouch}>
                  {t("courses.start_date")}
                </ResizableTh>
                <ResizableTh index={3} width={gWidths[3]} startResize={gStartResize} startResizeTouch={gStartResizeTouch}>
                  {t("courses.end_date")}
                </ResizableTh>
              </tr>
            </thead>
            <tbody>
              <tr data-testid={`${kind}-group-row`}>
                <td>
                  <input
                    value={group.name || ""}
                    onChange={(e) => setGroupField({ name: e.target.value })}
                    onBlur={flushGroup}
                    placeholder={nameLabel}
                    data-testid={`${kind}-grp-name`}
                  />
                </td>
                <td>
                  <input
                    value={group.dose || ""}
                    onChange={(e) => setGroupField({ dose: e.target.value })}
                    onBlur={flushGroup}
                    placeholder={t("courses.dose")}
                    data-testid={`${kind}-grp-dose`}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    value={group.start_date || ""}
                    onChange={(e) => setGroupField({ start_date: e.target.value })}
                    onBlur={flushGroup}
                    data-testid={`${kind}-grp-start`}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    value={group.end_date || ""}
                    onChange={(e) => setGroupField({ end_date: e.target.value })}
                    onBlur={flushGroup}
                    data-testid={`${kind}-grp-end`}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <label className="text-[11px] font-bold uppercase tracking-widest text-olive">
            {t("courses.remark")}
          </label>
          <textarea
            value={group.remark || ""}
            onChange={(e) => setGroupField({ remark: e.target.value })}
            onBlur={flushGroup}
            placeholder={t("courses.remark_placeholder")}
            rows={4}
            className="mt-1 w-full bg-white border border-app-border rounded px-3 py-2 text-sm text-espresso outline-none focus:border-olive resize-y"
            data-testid={`${kind}-grp-remark`}
          />
        </div>
      </div>
    </div>
  );
}

function CourseEndingsReminders({ supplements, recovery }) {
  const { t } = useI18n();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const collect = (data, kind, label) => {
    const out = [];
    const push = (name, endDate, where) => {
      if (!endDate) return;
      const d = new Date(endDate);
      if (Number.isNaN(d.getTime())) return;
      d.setHours(0, 0, 0, 0);
      const diff = Math.round((d - today) / 86400000);
      if (diff <= 5 && diff >= -3) {
        out.push({ name: name || label, where, diff, kind });
      }
    };
    (data?.individual || []).forEach((r) => push(r.name, r.end_date, `${t("courses.cage_no")} ${r.cage_no}`));
    if (data?.group) push(data.group.name, data.group.end_date, t("courses.section_group"));
    return out;
  };

  const items = [
    ...collect(supplements, "supplements", t("courses.tab_supplements")),
    ...collect(recovery, "recovery", t("courses.tab_recovery")),
  ].sort((a, b) => a.diff - b.diff);

  if (!items.length) return null;

  return (
    <div className="rounded-xl border border-olive/40 bg-olive/5 p-3" data-testid="course-endings-banner">
      <div className="text-xs font-bold uppercase tracking-widest text-olive mb-2">
        {t("courses.reminders_title")}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.slice(0, 12).map((it, i) => {
          const tone =
            it.diff < 0 ? "bg-espresso/10 text-espresso/60" :
            it.diff === 0 ? "bg-danger/15 text-danger" :
            "bg-olive/15 text-olive";
          const label =
            it.diff < 0 ? t("courses.ended") :
            it.diff === 0 ? t("courses.ends_today") :
            `${t("courses.ends_in")} ${it.diff} ${t("courses.days")}`;
          return (
            <div key={i} className={`text-[11px] px-2 py-1 rounded-md ${tone}`}>
              <span className="font-semibold">{it.name}</span>
              <span className="opacity-70"> · {it.where} · </span>
              <span>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CoursesPage() {
  const { activeGroup } = useGroups();
  const { t } = useI18n();
  const [tab, setTab] = useState("supplements");
  const [supData, setSupData] = useState(null);
  const [recData, setRecData] = useState(null);

  // Lightweight fetch for reminders banner (separate from the section's own fetch)
  const fetchSummary = useCallback(async () => {
    if (!activeGroup?.id) return;
    try {
      const [s, r] = await Promise.all([
        api.get("/courses/supplements"),
        api.get("/courses/recovery"),
      ]);
      setSupData(s.data);
      setRecData(r.data);
    } catch (_e) { void _e; }
  }, [activeGroup?.id]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return (
    <div className="space-y-5" data-testid="courses-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
            {t("courses.title")}
          </h1>
          <p className="text-sm text-espresso/70 mt-1">
            {activeGroup?.name ? <span className="text-olive font-semibold">{activeGroup.name}</span> : null}
            {activeGroup?.name ? " · " : ""}
            {t("courses.sub")}
          </p>
        </div>
      </div>

      <CourseEndingsReminders supplements={supData} recovery={recData} />

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-cream-2 rounded-lg p-1 border border-app-border w-fit">
        <button
          onClick={() => setTab("supplements")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            tab === "supplements" ? "bg-olive text-white" : "text-espresso/70 hover:bg-white"
          }`}
          data-testid="tab-supplements"
        >
          <Pill className="w-3.5 h-3.5" />
          {t("courses.tab_supplements")}
        </button>
        <button
          onClick={() => setTab("recovery")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            tab === "recovery" ? "bg-olive text-white" : "text-espresso/70 hover:bg-white"
          }`}
          data-testid="tab-recovery"
        >
          <Stethoscope className="w-3.5 h-3.5" />
          {t("courses.tab_recovery")}
        </button>
      </div>

      {tab === "supplements" ? (
        <CourseSection kind="supplements" />
      ) : (
        <CourseSection kind="recovery" />
      )}
    </div>
  );
}
